import { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "@emailjs/browser";
import { ORG } from "../data/initialData";

const EMAILJS_READY = !!(
  process.env.REACT_APP_EMAILJS_SERVICE_ID &&
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID &&
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY
);

const RECAPTCHA_READY = !!process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/* Simple in-session rate limit — max 3 submissions per browser session */
let sessionSubmissions = 0;
const MAX_SUBMISSIONS = 3;

const BLANK = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const [form,         setForm]         = useState(BLANK);
  const [sending,      setSending]      = useState(false);
  const [sent,         setSent]         = useState(false);
  const [err,          setErr]          = useState("");
  const [honeypot,     setHoneypot]     = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [rateLimited,  setRateLimited]  = useState(false);

  const formStart    = useRef(Date.now());
  const recaptchaRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const reset = () => {
    setSent(false);
    setForm(BLANK);
    setCaptchaToken(null);
    recaptchaRef.current?.reset();
    formStart.current = Date.now();
  };

  const send = async () => {
    setErr("");

    /* ── Bot traps ─────────────────────────────────────────────────── */
    /* 1. Honeypot field filled → silent fake success */
    if (honeypot) { setSent(true); return; }

    /* 2. Submitted too fast (< 3 s) → silent fake success */
    if (Date.now() - formStart.current < 3000) { setSent(true); return; }

    /* 3. Rate limiting → block after MAX_SUBMISSIONS */
    if (sessionSubmissions >= MAX_SUBMISSIONS) {
      setRateLimited(true);
      return;
    }

    /* ── Human validation ──────────────────────────────────────────── */
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErr("Please fill in your name, email address, and message.");
      return;
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) {
      setErr("Please enter a valid email address.");
      return;
    }

    if (RECAPTCHA_READY && !captchaToken) {
      setErr("Please complete the CAPTCHA verification below.");
      return;
    }

    /* ── Send ──────────────────────────────────────────────────────── */
    if (!EMAILJS_READY) {
      window.location.href =
        `mailto:${ORG.email}?subject=${encodeURIComponent(form.subject || "Enquiry")}` +
        `&body=${encodeURIComponent(form.message)}`;
      return;
    }

    setSending(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name:  form.name.trim(),
          from_email: form.email.trim(),
          subject:    form.subject.trim() || "Website Enquiry",
          message:    form.message.trim(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      sessionSubmissions += 1;
      setSent(true);
      setForm(BLANK);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch {
      setErr("Message could not be sent. Please email us directly at " + ORG.email);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSending(false);
    }
  };

  /* ── Rate-limited screen ─────────────────────────────────────────── */
  if (rateLimited) {
    return (
      <div>
        <div className="pg-hdr">
          <h1>Contact Us</h1>
          <p>Reach out to LHDA for inquiries about membership, the Project, or partnership opportunities.</p>
        </div>
        <section className="sec">
          <div className="container" style={{ maxWidth: 540, textAlign: "center" }}>
            <div style={{ background:"#fff3e0", border:"1.5px solid #ffb74d", borderRadius:16, padding:"2.5rem" }}>
              <div style={{ fontSize:"2.5rem", marginBottom:".75rem" }}>🚫</div>
              <div style={{ fontWeight:700, color:"#e65100", fontSize:"1.1rem", marginBottom:".5rem" }}>
                Too Many Submissions
              </div>
              <p style={{ color:"#5d4037", fontSize:".92rem", lineHeight:1.7 }}>
                You have reached the maximum number of contact form submissions for this session.
                Please email us directly.
              </p>
              <a
                href={`mailto:${ORG.email}`}
                className="btn-g"
                style={{ display:"inline-block", marginTop:"1.25rem", textDecoration:"none" }}
              >
                ✉️ Email {ORG.email}
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ── Main page ───────────────────────────────────────────────────── */
  return (
    <div>
      <div className="pg-hdr">
        <h1>Contact Us</h1>
        <p>Reach out to LHDA for inquiries about membership, the Project, or partnership opportunities.</p>
      </div>

      <section className="sec">
        <div className="container">
          <div className="contact-grid">

            {/* ── Contact details ── */}
            <div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"1.6rem", marginBottom:"1.5rem", color:"var(--gd)" }}>
                Get in Touch
              </h2>
              {[
                { ic:"📍", lbl:"Address",                    val: ORG.location },
                { ic:"✉️", lbl:"Email",                      val: ORG.email },
                { ic:"🏛️", lbl:"Incorporation No.",          val: ORG.incNo },
                { ic:"⚡", lbl:"Trustee Management Company", val: ORG.trustee },
                { ic:"👤", lbl:"LHL Managing Director",      val: ORG.lhlmd },
                { ic:"🌿", lbl:"Clans Represented",          val: ORG.clans },
                { ic:"🗺️", lbl:"Project Land",               val: ORG.land },
              ].map((c, i) => (
                <div key={i} className="contact-item">
                  <div className="contact-ic">{c.ic}</div>
                  <div>
                    <div className="contact-lbl">{c.lbl}</div>
                    <div className="contact-val">{c.val}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Contact form ── */}
            <div style={{ background:"var(--c)", borderRadius:16, padding:"2.5rem", border:"1.5px solid var(--br)" }}>
              <h3 style={{ fontFamily:"'Playfair Display',serif", marginBottom:"1.25rem", color:"var(--dk)" }}>
                Send a Message
              </h3>

              {!EMAILJS_READY && (
                <div className="info-banner">
                  📧 Email us directly at:{" "}
                  <a href={`mailto:${ORG.email}`} style={{ color:"var(--gd)", textDecoration:"underline" }}>
                    {ORG.email}
                  </a>
                </div>
              )}

              {sent ? (
                <div style={{ background:"#e8f5e9", border:"1.5px solid #a5d6a7", borderRadius:12, padding:"1.5rem", textAlign:"center" }}>
                  <div style={{ fontSize:"2rem", marginBottom:".5rem" }}>✅</div>
                  <div style={{ fontWeight:700, color:"#2e7d32", marginBottom:".35rem" }}>Message Sent!</div>
                  <div style={{ fontSize:".85rem", color:"#4e6058" }}>
                    We'll get back to you as soon as possible.
                  </div>
                  <button className="btn-g" style={{ marginTop:"1rem" }} onClick={reset}>
                    Send Another
                  </button>
                </div>
              ) : (
                <>
                  {err && (
                    <div className="login-err" style={{ marginBottom:"1rem" }}>{err}</div>
                  )}

                  {/* Honeypot — visually hidden, bots fill it in */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={e => setHoneypot(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position:"absolute", left:"-9999px", width:1, height:1, opacity:0 }}
                  />

                  {[
                    ["name",    "Full Name *",      "Your full name",       "text"],
                    ["email",   "Email Address *",  "your@email.com",       "email"],
                    ["subject", "Subject",          "Subject of your inquiry", "text"],
                  ].map(([k, l, ph, t]) => (
                    <div key={k} className="field">
                      <label>{l}</label>
                      <input
                        type={t}
                        placeholder={ph}
                        value={form[k]}
                        onChange={e => set(k, e.target.value)}
                        disabled={sending}
                      />
                    </div>
                  ))}

                  <div className="field">
                    <label>Message *</label>
                    <textarea
                      placeholder="Your message…"
                      rows={5}
                      value={form.message}
                      onChange={e => set("message", e.target.value)}
                      disabled={sending}
                    />
                  </div>

                  {/* ── reCAPTCHA ── */}
                  {RECAPTCHA_READY ? (
                    <div style={{ margin:"1rem 0" }}>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={token => { setCaptchaToken(token); setErr(""); }}
                        onExpired={() => setCaptchaToken(null)}
                        onError={() => { setCaptchaToken(null); setErr("CAPTCHA failed to load. Please refresh the page."); }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      margin:"1rem 0", padding:".6rem .9rem",
                      background:"#fff8e1", border:"1.5px solid #ffe082",
                      borderRadius:8, fontSize:".76rem", color:"#5d4037", fontWeight:600,
                    }}>
                      🔒 Basic spam protection active. Add a reCAPTCHA site key for stronger protection.
                    </div>
                  )}

                  <button
                    className="btn-g"
                    style={{ width:"100%", opacity: (RECAPTCHA_READY && !captchaToken) ? 0.55 : 1 }}
                    onClick={send}
                    disabled={sending || (RECAPTCHA_READY && !captchaToken)}
                  >
                    {sending ? "Sending…" : EMAILJS_READY ? "Send Message" : "Open Email Client →"}
                  </button>

                  <p style={{ fontSize:".76rem", color:"var(--gr)", marginTop:".75rem", textAlign:"center" }}>
                    Or email us directly at{" "}
                    <a href={`mailto:${ORG.email}`} style={{ color:"var(--gd)" }}>{ORG.email}</a>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
